// src/stores/user.ts
import {computed, inject, ref} from 'vue'
import { defineStore } from 'pinia'

import UserApi from '@/api/user'
import { accessTokenNeedsRefresh, readAccessTokenPayload } from '@/api/jwt-auth'

import type { UserLoginRequest } from '@/types/user'

import type { User } from '@/types/user'
import {routerKey} from "vue-router";

export type AuthenticationStatus =
  'unknown' | 'authenticating' | 'authenticated' | 'refreshing' | 'anonymous'

export const useUserStore = defineStore('user', () => {
  const router = inject(routerKey)
  const user = ref<User | null>(null)

  const authenticationStatus = ref<AuthenticationStatus>('unknown')

  const authenticationError = ref<string | null>(null)

  /*
   * Alle refresh-aanroepen binnen dit tabblad
   * wachten op dezelfde Promise.
   */
  let refreshPromise: Promise<boolean> | null = null

  const isAuthenticated = computed(
    () => authenticationStatus.value === 'authenticated' && user.value !== null,
  )

  const isAuthenticationPending = computed(
    () =>
      authenticationStatus.value === 'unknown' ||
      authenticationStatus.value === 'authenticating' ||
      authenticationStatus.value === 'refreshing',
  )

  function applyAccessToken(): boolean {
    const payload = readAccessTokenPayload()

    if (!payload) {
      user.value = null
      return false
    }

    user.value = payload
    authenticationStatus.value = 'authenticated'
    authenticationError.value = null

    return true
  }

  function clearSession(): void {
    user.value = null
    authenticationStatus.value = 'anonymous'
  }

  async function login(request: UserLoginRequest): Promise<User> {
    authenticationStatus.value = 'authenticating'
    authenticationError.value = null

    try {
      await UserApi.login(request)

      if (!applyAccessToken()) {
        throw new Error('Loginresponse bevat geen geldig access-token')
      }

      /*
       * applyAccessToken heeft user gevuld.
       */
      return user.value as User
    } catch (error: unknown) {
      clearSession()
      authenticationError.value = 'Inloggen is niet gelukt.'

      throw error
    }
  }

  async function performRefresh(): Promise<boolean> {
    authenticationStatus.value = 'refreshing'
    authenticationError.value = null

    try {
      await UserApi.refreshToken()

      if (!applyAccessToken()) {
        clearSession()
        return false
      }

      return true
    } catch {
      clearSession()
      return false
    }
  }

  function refreshSession(): Promise<boolean> {
    if (refreshPromise) {
      return refreshPromise
    }

    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null
    })

    return refreshPromise
  }

  async function ensureAuthenticated(): Promise<boolean> {
    const payload = readAccessTokenPayload()

    if (payload && !accessTokenNeedsRefresh()) {
      user.value = payload
      authenticationStatus.value = 'authenticated'

      return true
    }

    return refreshSession()
  }

  let restorePromise: Promise<boolean> | null = null
  function restoreSession(): Promise<boolean> {
    if (restorePromise) {
      return restorePromise
    }

    restorePromise = performRestoreSession().finally(() => {
      restorePromise = null
    })

    return restorePromise
  }

  async function performRestoreSession(): Promise<boolean> {
    authenticationStatus.value = 'unknown'
    authenticationError.value = null

    const payload = readAccessTokenPayload()

    if (payload && !accessTokenNeedsRefresh()) {
      user.value = payload
      authenticationStatus.value = 'authenticated'

      return true
    }

    return refreshSession()
  }

  async function logout(): Promise<void> {
    try {
      await UserApi.logout()
    } finally {
      clearSession()
      if (router?.currentRoute.value.meta.requiresAuth) {
        router.push({name: 'login'}).catch(() => {})
      }
    }
  }

  return {
    user,
    authenticationStatus,
    authenticationError,

    isAuthenticated,
    isAuthenticationPending,

    login,
    logout,
    restoreSession,
    ensureAuthenticated,
    refreshSession,
    clearSession,
    applyAccessToken,
  }
})
